import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { bidAnalysisServices, BidNote } from 'services/bidAnalysis/bidAnalysisServices';
import { isCurrentUser } from 'utils/userUtils';
import { formatDateForChartAPI } from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { SECTION_NAMES } from '../../constants/sectionNames';
import { BidPriceFillUpModal } from '../modals/BidPriceFillUpModal';
import NotesPanel, { NotesPanelRef } from '../NotesPanel';
import { SubSectionHeader } from '../shared/SubSectionHeader';
import {
  ContractPriceRankingItem,
  ContractPriceRankingTable,
} from '../tables/ContractPriceRankingTable';

interface ContractPriceRankingSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  bidId: number;
  concept: string;
  packSize: number;
  startDate?: string;
  endDate?: string;
  expandedRows: any;
  onRowToggle: (e: any) => void;
  isLoading?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  onFieldBlur?: (field: string, value: any) => void;
  onNotesChange?: () => void;
  notes?: BidNote[];
  currentUser?: string;
  bidOwner?: string | null;
  contractPriceRankingData?: Array<{
    ndc: string;
    contracter_name: string | null;
    labeller_name: string;
    price_type: string;
    price: number;
    price_eu: number;
    current_wac: number | null;
    current_market_share: string | null;
    extra_data: {
      contract_start_date: string | null;
      percentage_of_commercial_market: string | null;
      percentage_of_government_market: string | null;
      nad: string | null;
      nad_expiry: string | null;
    };
    price_rank: number;
  }>;
}

export const ContractPriceRankingSection: React.FC<ContractPriceRankingSectionProps> = observer(
  ({
    isExpanded,
    onToggle,
    bidId,
    concept,
    packSize,
    startDate,
    endDate,
    expandedRows,
    onRowToggle,
    isLoading = false,
    onFieldChange,
    onFieldBlur,
    onNotesChange,
    notes,
    currentUser = '',
    bidOwner = null,
    contractPriceRankingData,
  }) => {
    const { BidAnalysisStore } = useStore();
    const notesPanelRef = React.useRef<NotesPanelRef>(null);

    // Check if current user is the bid owner
    // Only show Price Against button if current user matches bid owner
    const _isOwner = isCurrentUser(currentUser, bidOwner);

    // Use data from prop if provided, otherwise fall back to store
    // This ensures we use fresh state data instead of potentially stale store data
    let contractPriceRanking: Array<any> = [];
    
    if (contractPriceRankingData && contractPriceRankingData.length > 0) {
      // Use fresh data from parent component state
      contractPriceRanking = contractPriceRankingData;
    } else {
      // Fallback to store if prop data not available
      const formattedStartDate = startDate ? formatDateForChartAPI(startDate) : undefined;
      const formattedEndDate = endDate ? formatDateForChartAPI(endDate) : undefined;
      
      contractPriceRanking =
        BidAnalysisStore.getContractPriceRankingData(bidId, concept, packSize, formattedStartDate, formattedEndDate) ||
        BidAnalysisStore.getContractPriceRankingData(bidId, concept, packSize) ||
        [];
    }

    // Modal state
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [selectedCompetitor, setSelectedCompetitor] = useState<ContractPriceRankingItem | null>(
      null
    );

    // Handle price against button click
    const handlePriceAgainstClick = (competitor: ContractPriceRankingItem) => {
      setSelectedCompetitor(competitor);
      setShowPriceModal(true);
    };

    // Handle price set from modal
    const handlePriceSet = async (price: number, noteText: string) => {
      if (!onFieldChange) {
        toast.error('Unable to update price: form handler not available', { autoClose: 5000 });
        return;
      }

      try {
        // Update suggested bid price
        onFieldChange('suggestedBidPrice', price);

        // Trigger recalculation by calling onFieldBlur
        // This ensures all dependent fields are recalculated (IFF Fee, GSMS Cost, GSMS Profit, WAC, etc.)
        if (onFieldBlur) {
          // Use setTimeout to ensure state from onFieldChange has been updated
          setTimeout(() => {
            onFieldBlur('suggestedBidPrice', price);
          }, 0);
        }

        // Create note automatically
        if (bidId && onNotesChange) {
          try {
            await bidAnalysisServices.createNote({
              bid_master_id: bidId,
              section_name: SECTION_NAMES.CONTRACT_PRICE_RANKING,
              content: noteText,
            });

            // Refresh notes
            await onNotesChange();

            toast.success('Bid price set and note added successfully', { autoClose: 5000 });
          } catch (noteError: unknown) {
            console.error('Error creating note:', noteError);
            // Still show success for price update even if note fails
            toast.warning('Bid price set, but note creation failed', { autoClose: 5000 });
          }
        } else {
          toast.success('Bid price set successfully', { autoClose: 5000 });
        }
      } catch (error: unknown) {
        console.error('Error setting bid price:', error);
        toast.error('Failed to set bid price', { autoClose: 5000 });
      }
    };

    const handleNotesClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (notesPanelRef.current) {
        notesPanelRef.current.show(event, event.currentTarget);
      }
    };

    const contractPriceRankingNotes = (notes || []).filter(
      note => note.section_name === SECTION_NAMES.CONTRACT_PRICE_RANKING
    );

    return (
      <div className={classes.subSection}>
        <SubSectionHeader
          title="Contract Price Ranking"
          isExpanded={isExpanded}
          onToggle={onToggle}
        >
          <Button
            icon="pi pi-comment"
            className={classes.notesButton}
            onClick={handleNotesClick}
            label={`Notes (${contractPriceRankingNotes.length})`}
            text
            size="small"
          />
        </SubSectionHeader>
        {isExpanded && (
          <div className={classes.subSectionContent}>
            <ContractPriceRankingTable
              data={contractPriceRanking}
              expandedRows={expandedRows}
              onRowToggle={onRowToggle}
              isLoading={isLoading}
              onPriceAgainstClick={
                onFieldChange && isCurrentUser(currentUser, bidOwner)
                  ? handlePriceAgainstClick
                  : undefined
              }
            />

            {/* Bid Price Fill-Up Modal */}
            {onFieldChange && (
              <BidPriceFillUpModal
                visible={showPriceModal}
                onHide={() => {
                  setShowPriceModal(false);
                  setSelectedCompetitor(null);
                }}
                competitor={selectedCompetitor}
                onPriceSet={handlePriceSet}
              />
            )}

            {/* Read-only Notes Panel */}
            <NotesPanel
              ref={notesPanelRef}
              bidId={bidId}
              sectionName={SECTION_NAMES.CONTRACT_PRICE_RANKING}
              notes={notes ?? []}
              onNotesChange={onNotesChange || (() => {})}
              currentUser={currentUser}
              readOnly={true}
            />
          </div>
        )}
      </div>
    );
  }
);
